// Maps mover-friendly onboarding answers to the payloads expected by the
// existing backend: PUT /api/movers/me, POST /api/moverPricing/me and
// PUT /api/calendar/availability. The backend models and engines are
// authoritative; this module only shapes the requests.
import { provinceName } from './geography.js';

export const SERVICE_OPTIONS = [
  { id: 'residential', label: 'Residential moving', description: 'Homes, condos and apartments' },
  { id: 'commercial', label: 'Commercial moving', description: 'Offices and business relocations' },
  { id: 'longDistance', label: 'Long-distance moving', description: 'Moves between cities or provinces' },
  { id: 'packing', label: 'Packing & unpacking', description: 'Help customers pack before moving day' },
  { id: 'specialty', label: 'Specialty items', description: 'Pianos, safes, appliances and other heavy items' },
  { id: 'assembly', label: 'Furniture assembly', description: 'Disassembly and reassembly of furniture' },
];

export const CREW_SIZES = [2, 3, 4, 5];
export const TRUCK_SIZES = ['16 ft', '20 ft', '24 ft', '26 ft'];
export const EQUIPMENT_OPTIONS = ['Liftgate', 'Moving blankets', 'Furniture dollies', 'Wardrobe boxes', 'Packing equipment', 'Specialty equipment'];

export const defaultAnswers = () => ({
  company: {
    companyName: '', legalName: '', contactName: '', email: '', phone: '',
    website: '', yearsInBusiness: '', description: '', address: '',
  },
  services: {
    residential: true, commercial: false, longDistance: false,
    packing: false, specialty: false, assembly: false,
  },
  serviceArea: {
    homeProvince: '', homeCity: '', localCities: [],
    longDistanceEnabled: false, destinationProvinces: [],
  },
  crews: {
    crewSizes: [2], trucks: [], extraMoverRate: '',
    equipment: [],
  },
  pricing: {
    chargeType: 'hourly', // hourly | flat | both
    crewRates: { 2: '', 3: '', 4: '', 5: '' },
    minimumHours: 2,
    minimumCharge: '',
    quoteValidityDays: 14,
    baseFee: '',
    perItemRate: '',
    longDistance: { mode: 'per_km', ratePerKm: '', ratePerMile: '', flatRate: '', minimumCharge: '' },
    additional: {
      stairs: { enabled: false, mode: 'flat', amount: '' },
      longCarry: { enabled: false, amount: '' },
      packing: { enabled: false, amount: '' },
      heavyItems: { enabled: false, amount: '' },
      specialty: { enabled: false, amount: '' },
      assembly: { enabled: false, amount: '' },
    },
  },
  availability: {
    timezone: 'America/Edmonton',
    bufferMinutes: 60,
    weekly: null, // null = use editor defaults
    exceptions: [],
  },
  documents: [],
});

// Hydrate wizard answers from an existing mover profile + MoverPricing doc.
export const answersFromProfile = (profile = {}, pricing = null, availability = null) => {
  const answers = defaultAnswers();
  answers.company = {
    companyName: profile.name || '',
    legalName: profile.legalName || '',
    contactName: profile.contactName || '',
    email: profile.email || '',
    phone: profile.phone || '',
    website: profile.website || '',
    yearsInBusiness: profile.yearsInBusiness ?? '',
    description: profile.description || '',
    address: profile.address || '',
  };
  const svc = new Set(Array.isArray(profile.services) ? profile.services : []);
  answers.services = {
    residential: profile.residentialMoves !== false,
    commercial: !!profile.commercialMoves,
    longDistance: !!profile.longDistanceMoves,
    packing: svc.has('packing'),
    specialty: svc.has('specialty'),
    assembly: svc.has('assembly'),
  };
  const locations = Array.isArray(profile.locations) ? profile.locations : [];
  const serviceArea = Array.isArray(profile.serviceArea) ? profile.serviceArea : [];
  answers.serviceArea = {
    homeProvince: Array.isArray(profile.provinces) && profile.provinces.length ? profile.provinces[0] : '',
    homeCity: locations[0] || serviceArea[0] || '',
    localCities: locations.slice(1),
    longDistanceEnabled: !!profile.longDistanceMoves,
    destinationProvinces: (Array.isArray(profile.provinces) ? profile.provinces : []).slice(1),
  };
  answers.crews = {
    crewSizes: Array.isArray(pricing?.crewPricing) && pricing.crewPricing.length
      ? pricing.crewPricing.map(c => c.crewSize)
      : (profile.crewSize ? [Number(profile.crewSize)] : [2]),
    trucks: Array.isArray(profile.truckTypes) ? profile.truckTypes : [],
    extraMoverRate: pricing?.baseRatePerMover ?? '',
    equipment: Array.isArray(profile.equipment) ? profile.equipment : [],
  };
  const surchargeByName = name => (pricing?.surcharges || []).find(s => String(s.name || '').toLowerCase().includes(name));
  const stairs = surchargeByName('stairs');
  const longCarry = surchargeByName('long carry');
  const packingFee = surchargeByName('packing');
  const heavy = (pricing?.specialItemFees || [])[0];
  const firstTier = (pricing?.distanceTiers || []).find(t => Number(t.minKm || 0) > 0);
  answers.pricing = {
    chargeType: pricing?.crewPricing?.length || pricing?.baseRatePerHour ? 'hourly' : (firstTier ? 'flat' : 'hourly'),
    crewRates: {
      2: pricing?.crewPricing?.find(c => c.crewSize === 2)?.hourlyRate ?? (pricing?.baseRatePerHour ?? profile.hourlyRate ?? ''),
      3: pricing?.crewPricing?.find(c => c.crewSize === 3)?.hourlyRate ?? '',
      4: pricing?.crewPricing?.find(c => c.crewSize === 4)?.hourlyRate ?? '',
      5: pricing?.crewPricing?.find(c => c.crewSize === 5)?.hourlyRate ?? '',
    },
    minimumHours: pricing?.minimumHours ?? profile.minimumHours ?? 2,
    minimumCharge: pricing?.minimumCharge ?? '',
    quoteValidityDays: pricing?.quoteValidityDays ?? 14,
    baseFee: profile.baseFee ?? pricing?.flatFees?.[0]?.amount ?? '',
    perItemRate: profile.perItemRate ?? '',
    longDistance: {
      mode: firstTier ? 'per_km' : (profile.perMileRate ? 'per_mile' : 'per_km'),
      ratePerKm: firstTier?.ratePerKm ?? pricing?.baseRatePerKm ?? '',
      ratePerMile: profile.perMileRate ?? '',
      flatRate: '',
      minimumCharge: firstTier?.minimumCharge ?? '',
    },
    additional: {
      stairs: { enabled: !!stairs?.enabled, mode: stairs?.type === 'per_floor' ? 'per_floor' : 'flat', amount: stairs?.amount ?? '' },
      longCarry: { enabled: !!longCarry?.enabled, amount: longCarry?.amount ?? '' },
      packing: { enabled: !!packingFee?.enabled, amount: packingFee?.amount ?? '' },
      heavyItems: { enabled: !!heavy?.enabled, amount: heavy?.amount ?? '' },
      specialty: { enabled: (pricing?.specialItemFees || []).some(f => f.enabled), amount: '' },
      assembly: { enabled: (pricing?.additionalServices || []).some(s => /assembly/i.test(s.name || '') && s.enabled), amount: (pricing?.additionalServices || []).find(s => /assembly/i.test(s.name || ''))?.price ?? '' },
    },
  };
  if (availability) {
    answers.availability = {
      timezone: availability.timezone || 'America/Edmonton',
      bufferMinutes: availability.bufferMinutes ?? 60,
      weekly: availability.weekly || null,
      exceptions: Array.isArray(availability.exceptions) ? availability.exceptions : [],
    };
  }
  return answers;
};

// ---- Mappers to backend payloads ----

const toNumberOrUndefined = value => (value === '' || value === null || value === undefined ? undefined : Number(value));

export const mapMoverUpdates = answers => {
  const services = ['moving'];
  if (answers.services.packing) services.push('packing');
  if (answers.services.specialty) services.push('specialty');
  if (answers.services.assembly) services.push('assembly');

  const provinces = [answers.serviceArea.homeProvince, ...(answers.services.longDistance ? answers.serviceArea.destinationProvinces : [])]
    .filter(Boolean)
    .map(code => String(code).toUpperCase());
  const locations = [answers.serviceArea.homeCity, ...answers.serviceArea.localCities].filter(Boolean);
  const provinceNames = provinces.map(provinceName);

  return {
    companyName: answers.company.companyName,
    legalName: answers.company.legalName,
    phone: answers.company.phone,
    website: answers.company.website,
    yearsInBusiness: toNumberOrUndefined(answers.company.yearsInBusiness),
    description: answers.company.description,
    address: answers.company.address,
    services,
    residentialMoves: !!answers.services.residential,
    commercialMoves: !!answers.services.commercial,
    longDistanceMoves: !!answers.services.longDistance,
    localMoves: true,
    crewSize: Math.min(...(answers.crews.crewSizes.length ? answers.crews.crewSizes : [2])),
    truckCount: answers.crews.trucks.length || undefined,
    equipment: answers.crews.equipment,
    provinces,
    locations,
    serviceArea: [...locations, ...provinceNames],
    // Legacy pricing fallbacks so validateMoverPricing resolves even before
    // the MoverPricing doc saves.
    hourlyRate: toNumberOrUndefined(answers.pricing.crewRates[2]) ?? toNumberOrUndefined(answers.pricing.longDistance.flatRate),
    minimumHours: toNumberOrUndefined(answers.pricing.minimumHours),
    perMileRate: answers.pricing.longDistance.mode === 'per_mile' ? toNumberOrUndefined(answers.pricing.longDistance.ratePerMile) : undefined,
    baseFee: toNumberOrUndefined(answers.pricing.baseFee),
    perItemRate: toNumberOrUndefined(answers.pricing.perItemRate),
  };
};

export const mapPricingPayload = answers => {
  const p = answers.pricing;
  const crewPricing = answers.crews.crewSizes
    .map(size => ({
      crewSize: size,
      hourlyRate: toNumberOrUndefined(p.crewRates[size]),
      minimumHours: toNumberOrUndefined(p.minimumHours),
    }))
    .filter(c => c.hourlyRate !== undefined && c.hourlyRate > 0);

  const firstCrewRate = crewPricing[0]?.hourlyRate;
  const payload = {
    minimumHours: toNumberOrUndefined(p.minimumHours),
    minimumCharge: toNumberOrUndefined(p.minimumCharge),
    quoteValidityDays: toNumberOrUndefined(p.quoteValidityDays) ?? 14,
    crewPricing,
    flatFees: toNumberOrUndefined(p.baseFee) !== undefined
      ? [{ name: 'Booking fee', amount: toNumberOrUndefined(p.baseFee), description: 'Standard booking and dispatch fee', enabled: true }]
      : [],
    surcharges: [],
    distanceTiers: [],
    specialItemFees: [],
    additionalServices: [],
  };
  if (toNumberOrUndefined(answers.crews.extraMoverRate) !== undefined) {
    payload.baseRatePerMover = toNumberOrUndefined(answers.crews.extraMoverRate);
  }

  if (p.chargeType !== 'flat' && firstCrewRate !== undefined) {
    payload.baseRatePerHour = firstCrewRate;
  }

  // Long-distance — only when the mover offers it
  if (answers.services.longDistance) {
    const ldMin = toNumberOrUndefined(p.longDistance.minimumCharge);
    if (p.longDistance.mode === 'per_km' && toNumberOrUndefined(p.longDistance.ratePerKm) !== undefined) {
      payload.baseRatePerKm = toNumberOrUndefined(p.longDistance.ratePerKm);
      payload.distanceTiers = [{ minKm: 50, maxKm: 99999, ratePerKm: toNumberOrUndefined(p.longDistance.ratePerKm), minimumCharge: ldMin ?? 0 }];
    } else if (p.longDistance.mode === 'per_mile' && toNumberOrUndefined(p.longDistance.ratePerMile) !== undefined) {
      payload.baseRatePerKm = Math.round((toNumberOrUndefined(p.longDistance.ratePerMile) / 1.60934) * 100) / 100;
      payload.distanceTiers = [{ minKm: 50, maxKm: 99999, ratePerKm: payload.baseRatePerKm, minimumCharge: ldMin ?? 0 }];
    } else if (p.longDistance.mode === 'flat' && toNumberOrUndefined(p.longDistance.flatRate) !== undefined) {
      payload.flatFees.push({ name: 'Long-distance flat rate', amount: toNumberOrUndefined(p.longDistance.flatRate), description: 'Flat rate for long-distance moves', enabled: true });
    }
  }

  const a = p.additional;
  if (a.stairs.enabled && toNumberOrUndefined(a.stairs.amount) !== undefined) {
    payload.surcharges.push({ name: 'Stairs', type: a.stairs.mode === 'per_floor' ? 'per_floor' : 'flat', amount: toNumberOrUndefined(a.stairs.amount), conditions: 'When stairs are required', enabled: true });
  }
  if (a.longCarry.enabled && toNumberOrUndefined(a.longCarry.amount) !== undefined) {
    payload.surcharges.push({ name: 'Long carry', type: 'flat', amount: toNumberOrUndefined(a.longCarry.amount), conditions: 'When parking is far from the entrance', enabled: true });
  }
  if (answers.services.packing && a.packing.enabled && toNumberOrUndefined(a.packing.amount) !== undefined) {
    payload.surcharges.push({ name: 'Packing required', type: 'flat', amount: toNumberOrUndefined(a.packing.amount), conditions: 'When customer requests packing help', enabled: true });
  }
  if (a.heavyItems.enabled && toNumberOrUndefined(a.heavyItems.amount) !== undefined) {
    payload.specialItemFees.push({ item: 'Heavy item', amount: toNumberOrUndefined(a.heavyItems.amount), description: 'Heavy or oversized item handling', enabled: true });
  }
  if (answers.services.specialty && a.specialty.enabled && toNumberOrUndefined(a.specialty.amount) !== undefined) {
    payload.specialItemFees.push({ item: 'Specialty item', amount: toNumberOrUndefined(a.specialty.amount), description: 'Pianos, safes and other specialty items', enabled: true });
  }
  if (answers.services.assembly && a.assembly.enabled && toNumberOrUndefined(a.assembly.amount) !== undefined) {
    payload.additionalServices.push({ name: 'Furniture assembly', description: 'Assembly or disassembly service', price: toNumberOrUndefined(a.assembly.amount), pricingType: 'flat', enabled: true });
  }

  return payload;
};

// Per-section completion — drives the wizard progress and the review step.
export const sectionCompletion = answers => {
  const c = answers.company;
  const company = Boolean(c.companyName.trim() && c.phone.trim());

  const services = Object.values(answers.services).some(Boolean);

  const sa = answers.serviceArea;
  const serviceArea = Boolean(sa.homeProvince && sa.homeCity)
    && (!answers.services.longDistance || sa.destinationProvinces.length > 0);

  const crews = answers.crews.crewSizes.length > 0;

  const p = answers.pricing;
  const hasRate = Object.values(p.crewRates).some(v => Number(v) > 0) || Number(p.longDistance.flatRate) > 0;
  const longDistanceOk = !answers.services.longDistance
    || (p.longDistance.mode === 'per_km' && Number(p.longDistance.ratePerKm) > 0)
    || (p.longDistance.mode === 'per_mile' && Number(p.longDistance.ratePerMile) > 0)
    || (p.longDistance.mode === 'flat' && Number(p.longDistance.flatRate) > 0)
    || p.longDistance.mode === 'custom';
  const pricing = hasRate && Number(p.minimumHours) > 0 && longDistanceOk;

  const weekly = answers.availability.weekly;
  const availability = !weekly || Object.values(weekly).some(windows => Array.isArray(windows) && windows.length > 0);

  const documents = answers.documents.length > 0;

  return { company, services, serviceArea, crews, pricing, availability, documents };
};
